import type { DesignSpec } from "@/types/designSpec";
import type { DesignSpecFieldChange } from "@/types/generatedImage";
import {
  getDesignSpecFieldLabel,
  getPresentableDesignChanges,
} from "./previewFreshness";

export interface DesignDetailRow {
  label: string;
  /** The displayed value, including "Removed" for a pending removal. */
  value: string;
  pending: boolean;
}

export interface DesignDetailsInput {
  side: "front" | "back";
  draft: DesignSpec | null;
  images: Record<
    "front" | "back",
    { url: string | null; designSpec: DesignSpec | null }
  >;
  changes: DesignSpecFieldChange[];
}

function readable(value: unknown): string {
  if (typeof value === "string") return value.replaceAll("_", " ").trim();
  if (Array.isArray(value))
    return value.map(readable).filter(Boolean).join(", ");
  return "";
}

function valueAtPath(
  spec: DesignSpec | null | undefined,
  path: string
): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === "object"
          ? (value as Record<string, unknown>)[key]
          : undefined,
      spec
    );
}

function readableDetail(path: string, value: unknown): string {
  if (path === "references" && Array.isArray(value)) {
    return value
      .map(reference => {
        if (!reference || typeof reference !== "object") return "";
        return [
          readable(reference.side),
          readable(reference.role),
          readable(reference.instruction),
        ]
          .filter(Boolean)
          .join(" · ");
      })
      .filter(Boolean)
      .join("; ");
  }
  return readable(value);
}

/**
 * Shared read-only Details presentation for the browser and customer lab.
 * Saved image contracts provide unchanged values; pending rows describe the
 * next preview. This projection never mutates either contract.
 */
export function projectDesignDetails({
  side,
  draft,
  images,
  changes,
}: DesignDetailsInput): {
  rows: DesignDetailRow[];
  pendingCount: number;
  hasSavedPreview: boolean;
} {
  const saved = images[side].designSpec;
  const spec = saved ?? draft;
  const frontSpec = images.front.url ? images.front.designSpec : draft;
  const backSpec = images.back.url ? images.back.designSpec : draft;
  const pending = new Map(
    getPresentableDesignChanges(changes).map(change => [change.path, change])
  );
  const definitions = [
    { label: "Color", paths: ["visual.primaryColor"], spec },
    { label: "Silhouette", paths: ["visual.silhouette"], spec },
    { label: "Fabric", paths: ["visual.fabric"], spec },
    { label: "Sleeves", paths: ["visual.sleeve"], spec },
    { label: "Front neckline", paths: ["visual.neckline"], spec: frontSpec },
    { label: "Length", paths: ["visual.length"], spec },
    { label: "Waist", paths: ["visual.waistline"], spec },
    {
      label: images.back.url ? "Back" : "Back direction",
      paths: ["visual.backPrompt"],
      spec: backSpec,
    },
    { label: "Back style", paths: ["visual.backStyle"], spec: backSpec },
    { label: "Back closure", paths: ["visual.closure"], spec: backSpec },
    { label: "Must have", paths: ["constraints.mustHave"], spec },
    { label: "Avoid", paths: ["constraints.avoid"], spec },
  ];
  const representedPaths = new Set(definitions.flatMap(field => field.paths));
  // Supplemental details stay visible after Apply clears their pending state.
  // Overall prose is shown only when it is the actual change, avoiding a
  // second description of the structured dress fields.
  const supplementalPaths = new Set([
    ...Object.keys(spec?.visual ?? {}).map(key => `visual.${key}`),
    ...Object.keys(spec?.constraints ?? {}).map(key => `constraints.${key}`),
    "occasion",
    "eventDate",
    "wearerContext",
    "references",
    "referenceGuidance",
    ...pending.keys(),
  ]);
  for (const path of supplementalPaths) {
    if (path === "visual.prompt" && !pending.has(path)) continue;
    if (!representedPaths.has(path)) {
      definitions.push({
        label: getDesignSpecFieldLabel(path),
        paths: [path],
        spec,
      });
    }
  }
  const rows = definitions
    .map(field => {
      const isPending = field.paths.some(path => pending.has(path));
      // Render plans filter references per side. Details represents the full
      // draft collection, including unchanged references for the other side.
      const values = field.paths.map(path =>
        readableDetail(
          path,
          pending.has(path)
            ? path === "references" && draft
              ? draft.references
              : pending.get(path)!.draftValue
            : valueAtPath(field.spec, path)
        )
      );
      return {
        label: field.label,
        value: [...new Set(values.filter(Boolean))].join(" · "),
        pending: isPending,
      };
    })
    .filter(field => field.value || field.pending)
    .map(field => ({ ...field, value: field.value || "Removed" }));
  return {
    rows,
    pendingCount: rows.filter(field => field.pending).length,
    hasSavedPreview: Boolean(saved),
  };
}
